import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Save, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = ["Submissions", "Projects", "Services"];
const STATUSES = ["new", "reviewing", "contacted", "won", "archived"];
const inp = "w-full rounded-sm border border-[#81754B]/40 bg-white px-3 py-2 text-sm text-[#3F3F2C] focus:border-[#66371B] focus:outline-none";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Submissions");
  const [stats, setStats] = useState({});
  const [subs, setSubs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/submissions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  };

  useEffect(() => { document.title = "Dashboard — Admin"; loadAll(); }, []);

  const doLogout = async () => { await logout(); navigate("/admin"); };

  const setSubStatus = async (id, status) => {
    setSubs((s) => s.map((x) => (x.id === id ? { ...x, status } : x)));
    await api.patch(`/submissions/${id}`, { status });
    toast.success("Status updated");
    api.get("/admin/stats").then((r) => setStats(r.data));
  };

  const saveProject = async (p) => {
    await api.put(`/admin/projects/${p.slug}`, {
      title: p.title, subtitle: p.subtitle, hero_image: p.hero_image,
      published: p.published, order: Number(p.order), featured: p.featured,
    });
    toast.success(`Saved ${p.title}`);
  };
  const delProject = async (slug) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${slug}`);
    setProjects((ps) => ps.filter((x) => x.slug !== slug));
    toast.success("Project deleted");
  };
  const saveService = async (s) => {
    await api.put(`/admin/services/${s.slug}`, { tagline: s.tagline, description: s.description });
    toast.success(`Saved ${s.title}`);
  };

  const upd = (setter) => (idx, key, val) => setter((arr) => arr.map((x, i) => (i === idx ? { ...x, [key]: val } : x)));
  const updP = upd(setProjects), updS = upd(setServices);

  return (
    <div className="min-h-screen bg-[#E3D8C1]/40">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F7F1E1]/90 border-b border-[#E3D8C1]">
        <div className="mx-auto max-w-[1400px] px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-[#3F3F2C]">seedesign<span className="text-[#66371B]"> / admin</span></Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#81754B] hidden sm:inline">{user?.name || user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-[#81754B]/40 px-4 py-2 text-sm text-[#3F3F2C] hover:border-[#66371B] hover:text-[#66371B] transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
          {[["Projects", stats.projects], ["Services", stats.services], ["Testimonials", stats.testimonials], ["Enquiries", stats.submissions], ["New", stats.new_submissions]].map(([l, v]) => (
            <div key={l} className="rounded-sm bg-[#F7F1E1] border border-[#E3D8C1] p-5">
              <p className="font-serif text-4xl text-[#66371B]">{v ?? "—"}</p>
              <p className="text-sm text-[#81754B] mt-1">{l}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
              className={`rounded-full px-5 py-2 text-sm transition-colors ${tab === t ? "bg-[#3F3F2C] text-[#F7F1E1]" : "bg-[#F7F1E1] text-[#3F3F2C] border border-[#E3D8C1] hover:border-[#66371B]"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Submissions" && (
          <div className="space-y-4" data-testid="admin-submissions">
            {subs.length === 0 && <p className="text-[#81754B]">No enquiries yet.</p>}
            {subs.map((s) => (
              <div key={s.id} className="rounded-sm bg-[#F7F1E1] border border-[#E3D8C1] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-2xl text-[#3F3F2C]">{s.name} <span className="text-base text-[#81754B]">· {s.company || "—"}</span></p>
                    <p className="text-sm text-[#66371B] font-medium">☎ {s.phone || "—"}</p>
                    <p className="text-sm text-[#81754B]">{s.email || "no email"} · {s.project_type || "call-back"} · {s.budget || "—"}</p>
                  </div>
                  <select value={s.status} onChange={(e) => setSubStatus(s.id, e.target.value)}
                    data-testid={`sub-status-${s.id}`} className="rounded-sm border border-[#81754B]/40 bg-white px-3 py-1.5 text-sm">
                    {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                {s.goals && <p className="mt-3 text-sm text-[#3F3F2C]"><b>Goals:</b> {s.goals}</p>}
                {s.challenges && <p className="mt-1 text-sm text-[#3F3F2C]"><b>Challenges:</b> {s.challenges}</p>}
                <p className="mt-2 text-xs text-[#81754B]">{new Date(s.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "Projects" && (
          <div className="space-y-4" data-testid="admin-projects">
            {projects.map((p, i) => (
              <div key={p.slug} className="rounded-sm bg-[#F7F1E1] border border-[#E3D8C1] p-6">
                <div className="grid md:grid-cols-[1fr_1fr] gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.12em] text-[#81754B]">Title</label>
                    <input className={inp} value={p.title} onChange={(e) => updP(i, "title", e.target.value)} data-testid={`proj-title-${p.slug}`} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.12em] text-[#81754B]">Subtitle</label>
                    <input className={inp} value={p.subtitle || ""} onChange={(e) => updP(i, "subtitle", e.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs uppercase tracking-[0.12em] text-[#81754B]">Hero image URL</label>
                    <input className={inp} value={p.hero_image || ""} onChange={(e) => updP(i, "hero_image", e.target.value)} />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-[#3F3F2C]">
                    <input type="checkbox" checked={!!p.published} onChange={(e) => updP(i, "published", e.target.checked)} /> Published
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#3F3F2C]">
                    <input type="checkbox" checked={!!p.featured} onChange={(e) => updP(i, "featured", e.target.checked)} /> Featured
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#3F3F2C]">Order
                    <input type="number" className="w-16 rounded-sm border border-[#81754B]/40 bg-white px-2 py-1 text-sm" value={p.order} onChange={(e) => updP(i, "order", e.target.value)} />
                  </label>
                  <a href={`/work/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#81754B] hover:text-[#66371B]"><ExternalLink size={14} /> View</a>
                  <div className="ml-auto flex gap-2">
                    <button onClick={() => saveProject(p)} data-testid={`proj-save-${p.slug}`}
                      className="inline-flex items-center gap-2 rounded-full bg-[#81754B] px-4 py-2 text-sm text-[#F7F1E1] hover:bg-[#3F3F2C]"><Save size={14} /> Save</button>
                    <button onClick={() => delProject(p.slug)} data-testid={`proj-del-${p.slug}`}
                      className="inline-flex items-center gap-2 rounded-full border border-[#66371B] px-4 py-2 text-sm text-[#66371B] hover:bg-[#66371B] hover:text-[#F7F1E1]"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "Services" && (
          <div className="space-y-4" data-testid="admin-services">
            {services.map((s, i) => (
              <div key={s.slug} className="rounded-sm bg-[#F7F1E1] border border-[#E3D8C1] p-6">
                <p className="font-serif text-xl text-[#3F3F2C] mb-3">{s.title}</p>
                <label className="text-xs uppercase tracking-[0.12em] text-[#81754B]">Tagline</label>
                <input className={inp} value={s.tagline || ""} onChange={(e) => updS(i, "tagline", e.target.value)} />
                <label className="text-xs uppercase tracking-[0.12em] text-[#81754B] mt-3 block">Description</label>
                <textarea rows={2} className={inp} value={s.description || ""} onChange={(e) => updS(i, "description", e.target.value)} />
                <button onClick={() => saveService(s)} data-testid={`svc-save-${s.slug}`}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#81754B] px-4 py-2 text-sm text-[#F7F1E1] hover:bg-[#3F3F2C]"><Save size={14} /> Save</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
